import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { RefreshCw, Wallet, FileWarning } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { getAdvanceRecoveryLog } from '../../services/api';

const fmtAED = (n) => `AED ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const RecoveryLogDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdvanceRecoveryLog();
      setRecords(res.data.records || []);
      setTotal(res.data.total_amount || 0);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load recovery log', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="recovery-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-amber-600" /> Advance Recovery Log
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">
            Finance trail of every advance leave-salary recovery HR acknowledged on an early exit.
          </p>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="recovery-log-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {!loading && records.length > 0 && (
          <div className="flex justify-end mb-2">
            <span className="text-sm font-semibold" data-testid="recovery-log-total">
              Total recovered: {fmtAED(total)}
            </span>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading…</div>
        ) : records.length === 0 ? (
          <div className="py-12 text-center" data-testid="recovery-log-empty">
            <FileWarning className="h-10 w-10 mx-auto text-gray-400 mb-2" />
            <p className="font-medium">No recoveries logged yet</p>
            <p className="text-sm text-muted-foreground">Acknowledged advance recoveries will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="recovery-log-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b bg-muted/40">
                  <th className="p-2 font-medium">Employee</th>
                  <th className="p-2 font-medium">Last Working Day</th>
                  <th className="p-2 font-medium text-right">Days</th>
                  <th className="p-2 font-medium text-right">Amount</th>
                  <th className="p-2 font-medium">Acknowledged By</th>
                  <th className="p-2 font-medium">On</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-muted/20" data-testid={`recovery-log-row-${r.id}`}>
                    <td className="p-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-xs text-muted-foreground">{r.employee_code}</div>
                    </td>
                    <td className="p-2">{r.last_working_date}</td>
                    <td className="p-2 text-right">{r.recovery_days}</td>
                    <td className="p-2 text-right font-semibold text-rose-700">{fmtAED(r.recovery_amount)}</td>
                    <td className="p-2">{r.acknowledged_by}</td>
                    <td className="p-2 text-xs text-muted-foreground">{(r.acknowledged_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecoveryLogDialog;
